import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BookOpen } from "lucide-react";
import { useToast } from "../Context/ToastContext";
import { getApiErrorDetail } from "../../utils/idempotency";
import TdsReportsWelcome from "../TDS/components/TdsReportsWelcome";
import TdsPayrollReportFilters from "./TdsPayrollReportFilters";
import TdsReportsActionBar from "../TDS/components/TdsReportsActionBar";
import TdsReportsDataGrid from "../TDS/components/TdsReportsDataGrid";
import TdsReportsExportDrawer from "../TDS/components/TdsReportsExportDrawer";
import TdsReportsGuidePanel from "../TDS/components/TdsReportsGuidePanel";
import TdsReportsHeader from "../TDS/components/TdsReportsHeader";
import TdsReportsProgress from "../TDS/components/TdsReportsProgress";
import TdsStatCard from "../TDS/components/TdsStatCard";
import { getSelectedOrgRegime } from "../TDS/tdsStorage";
import { GENERATION_STEPS } from "../TDS/tdsReportsConfig";
import { formatINR } from "../TDS/tdsConstants";
import { buildTdsReportRows, exportRowsToXlsx } from "./payrollReportsUtils";
import { IndianRupee, Users, Wallet } from "lucide-react";
import "../TDS/tds-reports.css";

const parseNum = (v) => {
  const n = Number(String(v ?? "").replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) ? n : 0;
};

const currentCalMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

function regimeDisplay(regime) {
  if (regime === "old") return "Old Regime";
  if (regime === "new") return "New Regime";
  return regime || "—";
}

const GRID_COLUMNS = [
  {
    key: "Employee",
    label: "Employee",
    render: (row) => row.Employee,
  },
  {
    key: "Taxable Income",
    label: "Taxable Income",
    numeric: true,
    render: (row) => row["Taxable Income"],
  },
  {
    key: "TDS Deducted",
    label: "TDS Deducted",
    numeric: true,
    render: (row) => row["TDS Deducted"],
  },
];

export default function TdsPayrollReportPanel({
  generatedBy,
  employees,
  departments,
  financialYears,
  companyName = "Organization",
}) {
  const toast = useToast();
  const reportRef = useRef(null);
  const [phase, setPhase] = useState("welcome");
  const [runId, setRunId] = useState("");
  const [runLabel, setRunLabel] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [calMonth, setCalMonth] = useState(currentCalMonth());
  const [fy, setFy] = useState(financialYears?.current || "");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [progressStep, setProgressStep] = useState(-1);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [guideMobileOpen, setGuideMobileOpen] = useState(false);
  const [lastGenerated, setLastGenerated] = useState(null);

  const orgRegime = getSelectedOrgRegime();

  useEffect(() => {
    if (financialYears?.current && !fy) setFy(financialYears.current);
  }, [financialYears, fy]);

  const filteredRows = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter((r) => String(r.Employee || "").toLowerCase().includes(q));
  }, [rows, search]);

  const summary = useMemo(() => {
    if (!hasGenerated || !filteredRows.length) return null;
    const taxable = filteredRows.reduce((s, r) => s + parseNum(r["Taxable Income"]), 0);
    const tds = filteredRows.reduce((s, r) => s + parseNum(r["TDS Deducted"]), 0);
    const withTds = filteredRows.filter((r) => parseNum(r["TDS Deducted"]) > 0).length;
    return { rowCount: filteredRows.length, withTds, taxable, tds };
  }, [hasGenerated, filteredRows]);

  const totals = useMemo(() => {
    if (!summary) return null;
    return {
      "Taxable Income": formatINR(summary.taxable),
      "TDS Deducted": formatINR(summary.tds),
    };
  }, [summary]);

  const inr = (n) => formatINR(n);

  const runReport = useCallback(async () => {
    if (!runId) {
      toast.error("Select a payroll month first.");
      return;
    }
    setGenerating(true);
    setLoading(true);
    setProgressStep(0);
    try {
      for (let i = 0; i < GENERATION_STEPS.length - 1; i += 1) {
        setProgressStep(i);
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 160));
      }
      const data = await buildTdsReportRows({ runId, employees });
      setRows(data);
      setHasGenerated(true);
      setProgressStep(GENERATION_STEPS.length - 1);
      setLastGenerated({ at: new Date().toISOString(), by: generatedBy });
      toast.success("TDS report generated.");
      reportRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    } catch (e) {
      setRows([]);
      setHasGenerated(false);
      setProgressStep(-1);
      toast.error(getApiErrorDetail(e) || "Failed to generate TDS report.");
    } finally {
      setLoading(false);
      setGenerating(false);
      setTimeout(() => setProgressStep(-1), 1200);
    }
  }, [runId, employees, generatedBy, toast]);

  const handleReset = () => {
    setRunId("");
    setRunLabel("");
    setDepartmentId("");
    setCalMonth(currentCalMonth());
    setFy(financialYears?.current || "");
    setSearch("");
    setRows([]);
    setHasGenerated(false);
    setLastGenerated(null);
    toast.success("Filters reset.");
  };

  const exportCsv = () => {
    if (!filteredRows.length) {
      toast.error("No records to export.");
      return;
    }
    const cols = Object.keys(filteredRows[0]);
    const csv = [
      cols.join(","),
      ...filteredRows.map((r) => cols.map((c) => `"${String(r[c] ?? "").replace(/"/g, '""')}"`).join(",")),
    ].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `tds_report_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported.");
  };

  const exportExcel = () => {
    if (!filteredRows.length) {
      toast.error("No records to export.");
      return;
    }
    exportRowsToXlsx(filteredRows, "TDS Report", "tds_report.xlsx");
    toast.success("Excel exported.");
  };

  const handlePrint = () => {
    if (!hasGenerated) {
      toast.error("Generate the report before printing.");
      return;
    }
    window.print();
  };

  return (
    <div className="tdsr-page">
      <TdsReportsHeader
        companyName={companyName}
        financialYear={fy}
        payrollMonth={runLabel || "—"}
        regimeLabel={regimeDisplay(orgRegime)}
        lastGenerated={lastGenerated}
        onGenerate={runReport}
        onExport={() => setExportOpen(true)}
        onPrint={handlePrint}
        onHelp={() => setGuideMobileOpen(true)}
        generating={generating}
      />

      <div className="tdsr-shell">
        <main className="tdsr-main">
          {phase === "welcome" ? (
            <TdsReportsWelcome
              onStart={() => setPhase("workspace")}
              title="TDS Report"
              description="Taxable income and TDS deducted per employee from finalized payroll runs. Configure filters and generate when ready."
              supported={[
                "Employee-wise taxable income",
                "Monthly TDS deductions",
                "Department filtering",
                "Excel, CSV & PDF export",
                "Print-ready layout",
              ]}
              ctaLabel="Start Report Generation"
            />
          ) : (
            <>
              <TdsPayrollReportFilters
                runId={runId}
                runLabel={runLabel}
                onRunChange={(id, label) => {
                  setRunId(id);
                  setRunLabel(label);
                }}
                departmentId={departmentId}
                onDepartmentChange={setDepartmentId}
                departments={departments}
                calMonth={calMonth}
                onCalMonthChange={setCalMonth}
                fy={fy}
                onFyChange={setFy}
                fyOptions={financialYears?.options || []}
                search={search}
                onSearchChange={setSearch}
              />

              <TdsReportsActionBar
                onGenerate={runReport}
                onPreview={() => reportRef.current?.scrollIntoView({ behavior: "smooth" })}
                onReset={handleReset}
                onExport={() => setExportOpen(true)}
                onPrint={handlePrint}
                onHelp={() => setGuideMobileOpen(true)}
                generating={generating}
                disabled={!runId}
              />

              {progressStep >= 0 && <TdsReportsProgress stepIndex={progressStep} />}

              {hasGenerated && summary && (
                <section className="tdsr-summary" aria-label="Report summary">
                  <TdsStatCard
                    label="Total Employees"
                    value={summary.rowCount}
                    icon={Users}
                    accent="#7c3aed"
                    bg="#ede9fe"
                    color="#5b21b6"
                  />
                  <TdsStatCard
                    label="Employees with TDS"
                    value={summary.withTds}
                    icon={Wallet}
                    accent="#059669"
                    bg="#d1fae5"
                    color="#047857"
                  />
                  <TdsStatCard
                    label="Taxable Income"
                    value={inr(summary.taxable)}
                    icon={IndianRupee}
                    accent="#2563eb"
                    bg="#dbeafe"
                    color="#1d4ed8"
                  />
                  <TdsStatCard
                    label="TDS Deducted"
                    value={inr(summary.tds)}
                    icon={IndianRupee}
                    accent="#dc2626"
                    bg="#fee2e2"
                    color="#b91c1c"
                  />
                </section>
              )}

              <div ref={reportRef}>
                <section className="tdsr-report-panel">
                  <div className="tdsr-report-panel__head">
                    <h2 className="tdsr-report-panel__title">TDS Report</h2>
                    <p className="tdsr-report-panel__sub">
                      Taxable income and TDS deducted per employee · {runLabel || "Select payroll month"}
                    </p>
                  </div>
                  <TdsReportsDataGrid
                    columns={GRID_COLUMNS}
                    rows={hasGenerated ? filteredRows : []}
                    loading={loading}
                    emptyTitle="No report has been generated yet."
                    emptyHint="Choose a payroll month above and click Generate Report."
                    onGenerate={runReport}
                    totals={hasGenerated ? totals : null}
                  />
                </section>
              </div>

              <button
                type="button"
                className="tds-btn tds-btn--secondary tdsr-guide-mobile-trigger"
                onClick={() => setGuideMobileOpen(true)}
              >
                <BookOpen size={16} />
                Open User Guide
              </button>
            </>
          )}
        </main>

        <TdsReportsGuidePanel className="tdsr-guide--desktop" />
      </div>

      {guideMobileOpen && (
        <TdsReportsGuidePanel
          className="tdsr-guide--mobile tdsr-guide--open"
          onMobileClose={() => setGuideMobileOpen(false)}
        />
      )}

      <TdsReportsExportDrawer
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        onExcel={() => {
          exportExcel();
          setExportOpen(false);
        }}
        onCsv={() => {
          exportCsv();
          setExportOpen(false);
        }}
        onPdf={handlePrint}
        onPrint={handlePrint}
      />
    </div>
  );
}
